import React, { useEffect } from 'react';
import { Settings, Save, RotateCcw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import useModuleConfigTheme from '../../hooks/useModuleConfigTheme';
import { AppNotificationToast, useAppNotificationToast } from '../AppNotificationToast';

const DEFAULT_GRADIENTS = [
  'from-[#3a624e] to-[#2e452e]',
  'from-[#a67c3d] to-[#8a6530]',
  'from-emerald-500 to-emerald-600',
  'from-teal-500 to-teal-600',
  'from-amber-500 to-amber-600',
  'from-violet-500 to-violet-600',
];

/**
 * Module settings shell: title left, custom category tab pills on the right.
 */
const ModuleConfigPageLayout = ({
  title,
  subtitle,
  titleIcon: TitleIcon = Settings,
  tabs = [],
  activeTab,
  onTabChange,
  tabContent = {},
  saved,
  savedMessage,
  saveError,
  loading,
  saving,
  onSave,
  onReset,
  showSaveActions = true,
  headerActions,
  children,
  tabLabels,
  configContent,
  primesContent,
  codesContent,
  formulesContent,
}) => {
  const { t } = useTranslation();
  const theme = useModuleConfigTheme();
  const { notification, show: showNotify, dismiss } = useAppNotificationToast();

  const resolvedTabs = tabs.length > 0
    ? tabs
    : [
        { id: 'config', label: tabLabels?.config ?? t('configPage.tab_config', 'الإعداد'), icon: Settings },
        { id: 'primes', label: tabLabels?.primes ?? t('configPage.tab_bonuses', 'مكافآت') },
        { id: 'codes', label: tabLabels?.codes ?? t('configPage.tab_codes', 'الرموز') },
        { id: 'formules', label: tabLabels?.formules ?? t('configPage.tab_formulas', 'الصيغ') },
      ];

  const resolvedContent = tabs.length > 0
    ? tabContent
    : {
        config: configContent,
        primes: primesContent,
        codes: codesContent,
        formules: formulesContent,
      };

  useEffect(() => {
    if (saved && savedMessage) {
      showNotify({
        message: savedMessage,
        variant: 'success',
        status: 'success',
      });
    }
  }, [saved, savedMessage, showNotify]);

  useEffect(() => {
    if (saveError) {
      showNotify({
        message: saveError,
        variant: 'error',
        status: 'error',
      });
    }
  }, [saveError, showNotify]);

  if (loading) {
    return (
      <div className="space-y-6">
        <p className={theme.subtitle}>{t('common.loading')}</p>
      </div>
    );
  }

  const activePanel = resolvedContent[activeTab];

  const saveBtnClass = theme.light
    ? 'px-5 py-2.5 bg-white hover:bg-emerald-50 text-emerald-900 border border-emerald-300/70 rounded-xl flex items-center gap-2 font-semibold shadow-sm disabled:opacity-50 transition-colors'
    : 'px-5 py-2.5 bg-white hover:bg-white/95 text-forest-900 border border-white/30 rounded-xl flex items-center gap-2 font-semibold shadow-sm disabled:opacity-50 transition-colors';

  const resetBtnClass = theme.light
    ? 'px-5 py-2.5 bg-white hover:bg-slate-50 text-slate-800 border border-slate-300/80 rounded-xl flex items-center gap-2 font-medium shadow-sm transition-colors'
    : 'px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white border border-white/25 rounded-xl flex items-center gap-2 font-medium transition-colors';

  return (
    <div className="space-y-6">
      <AppNotificationToast notification={notification} onDismiss={dismiss} />

      <div className="flex flex-col gap-4 xl:flex-row xl:justify-between xl:items-start">
        <div className="min-w-0">
          <h1 className={`text-3xl font-bold flex items-center gap-3 ${theme.title}`}>
            <TitleIcon className="h-8 w-8 shrink-0" />
            {title}
          </h1>
          {subtitle ? <p className={`mt-2 ${theme.subtitle}`}>{subtitle}</p> : null}
        </div>
        <div className="flex flex-wrap gap-2 xl:justify-end xl:max-w-[60%]">
          {resolvedTabs.map((tab, index) => {
            const Icon = tab.icon || Settings;
            const isActive = activeTab === tab.id;
            const gradient = tab.gradient || DEFAULT_GRADIENTS[index % DEFAULT_GRADIENTS.length];
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => onTabChange(tab.id)}
                className={`qawaem-module-config-tab px-4 py-3 rounded-xl flex items-center gap-2 font-medium transition-all whitespace-nowrap ${
                  isActive
                    ? `bg-gradient-to-r ${gradient} text-white force-white shadow-lg`
                    : theme.tabInactive
                }`}
              >
                <Icon className="h-5 w-5 shrink-0 force-white text-white" />
                <span className="force-white text-white">{tab.label}</span>
              </button>
            );
          })}
          {headerActions}
        </div>
      </div>

      {children}

      {activePanel !== undefined && activePanel !== null ? (
        <div className="animate-fade-in space-y-6">
          {activePanel}
          {showSaveActions && onSave ? (
            <div className="flex flex-wrap gap-3 pt-2 justify-end">
              {onReset ? (
                <button
                  type="button"
                  onClick={onReset}
                  className={resetBtnClass}
                >
                  <RotateCcw className="h-4 w-4" />
                  {t('common.reset', 'إعادة ضبط')}
                </button>
              ) : null}
              <button
                type="button"
                onClick={onSave}
                disabled={saving}
                className={saveBtnClass}
              >
                <Save className="h-4 w-4" />
                {saving ? t('common.saving') : t('common.save')}
              </button>
            </div>
          ) : null}
        </div>
      ) : (
        <div className={theme.card}>
          <p className={theme.subtitle}>{t('configPage.coming_soon', 'قريباً')}</p>
        </div>
      )}
    </div>
  );
};

export default ModuleConfigPageLayout;
